<template>
  <div class="input-group daypickr flex" v-feature-flag="'dtpBtn'">
    <dtpBtn
      flat
      round
      dense
      icon="chevron_left"
      @click="datepickerDec"
      class="noprint"
    />
    <FlatPickr
      v-feature-flag="'dtpBtn'"
      :modelValue="displayDate"
      @on-change="flatdateChanged"
      :config="config"
      placeholder="Select date"
      name="day"
    />
    <CuiButtonDropdown v-feature-flag="'!dtpBtn'">
      <template #label>{{ formattedDateWithWeek }}</template>
      <CuiDatePicker
        v-model="displayDate"
        :config="config"
        @on-change="flatdateChanged"
        placeholder="Select week"
        wcag-label="Select week"
        name="week"
        inline
      />
    </CuiButtonDropdown>
    <dtpBtn
      icon="chevron_right"
      class="noprint -ml-3"
      iconOnly
      @click="datepickerAdd"
    />
  </div>
  <CuiButtonGroup v-feature-flag="'!dtpBtn'">
    <CuiButton icon="chevron_left" iconOnly ariaLabel="Previous" @click="datepickerDec" />
    <CuiButtonDropdown>
      <template #label>{{ formattedDateWithWeek }}</template>
      <CuiDatePicker
        v-model="displayDate"
        :config="config"
        @on-change="flatdateChanged"
        placeholder="Select week"
        wcag-label="Select week"
        name="week"
        inline
      />
    </CuiButtonDropdown>
    <CuiButton icon="chevron_right" iconOnly ariaLabel="Next" @click="datepickerAdd" />
  </CuiButtonGroup>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { isSameDay, addDays, format, getWeek, getYear } from "date-fns";
import i18n from "../i18n";
import { getFlatpickrLocale } from "../utils/flatpickrLocale";
import { CuiButtonDropdown } from "@ist/commonui-components";

defineOptions({ name: "IstToolbarDate" });

const windowWidth = ref(window.innerWidth);
const onResize = () => {
  windowWidth.value = window.innerWidth;
};
onMounted(() => window.addEventListener("resize", onResize));
onUnmounted(() => window.removeEventListener("resize", onResize));

const props = withDefaults(
  defineProps<{ modelValue?: Date | null; disabled?: boolean }>(),
  {
    modelValue: null,
    disabled: false,
  },
);
const emit = defineEmits<{
  (e: "change", date: Date): void;
  (e: "input", date: Date): void;
  (e: "update:modelValue", date: Date): void;
}>();

const displayDate = ref<Date>(new Date());

const formattedDateWithWeek = computed(() => {
  if (!displayDate.value) return "";
  if (windowWidth.value < 600) {
    const isCurrentYear = getYear(displayDate.value) === getYear(new Date());
    return format(displayDate.value, isCurrentYear ? "dd-MM" : "dd-MM-yy");
  }
  const dateStr = format(displayDate.value, "dd-MM-yyyy");
  const weekNumber = getWeek(displayDate.value, { weekStartsOn: 1 });
  const weekLabel = i18n.global.t("week").toString().toLowerCase();
  return `${dateStr} ( ${weekLabel} ${weekNumber} )`;
});

const config = ref<any>({
  week: { weekNumbers: true },
  wrap: true,
  altFormat:
    "d-m-y ( " + i18n.global.t("actualPlan.weekescaped").toString() + " W )",
  altInput: true,
  dateFormat: "d-m-Y",
  weekNumbers: true,
  plugins: [],
  locale: getFlatpickrLocale(),
});

watch(
  () => (i18n.global.locale as unknown as { value: string }).value,
  () => {
    config.value = { ...config.value, locale: getFlatpickrLocale() };
  },
);

onMounted(() => {
  displayDate.value = props.modelValue || new Date();
});

const dateChanged = (date: Date) => {
  emit("change", date);
  emit("input", date);
  emit("update:modelValue", date);
};

const flatdateChanged = (date: Date[]) => {
  if (!isSameDay(displayDate.value, date[0])) {
    displayDate.value = date[0];
    dateChanged(date[0]);
  }
};

const datepickerAdd = () => {
  displayDate.value = addDays(props.modelValue || new Date(), 1);
  dateChanged(displayDate.value);
};
const datepickerDec = () => {
  displayDate.value = addDays(props.modelValue || new Date(), -1);
  dateChanged(displayDate.value);
};
</script>
<style lang="scss" scoped>
@import "../assets/variables";

.input-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.daypickr {
  :deep(.flatpickr-input) {
    color: $ist-blue !important;
    border: none;
    text-align: center;
    cursor: pointer;
  }
}

.daypickr {
  :deep(.form-control) {
    color: $ist-blue !important;
    border: none;
    text-align: center;
    cursor: pointer;

    @media (max-width: 600px) {
      width: 185px;
    }
  }
}
</style>
